import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import {
  SETTINGS_DRAWER_BOOLEANS,
  useSettingsDrawerBoolean,
} from "components/Header/useSettingsDrawerBooleans";

const ChatHandleContext = createContext();

export const ChatHandleProvider = (props) => {
  const [chatHandle, setChatHandle] = useState(null);
  const [isLoadingChatHandle, setIsLoadingChatHandle] = useState(false);
  const [chatHandleError, setChatHandleError] = useState(null);
  // const { read, write, isDBReady } = useIndexDB();
  const { user } = useAuth();
  const userId = user?.id;
  const isChatHandleEnabled = useSettingsDrawerBoolean(
    SETTINGS_DRAWER_BOOLEANS.CHAT_RECORDER_ENABLED
  );

  const populateChatHandle = useCallback(async () => {
    try {
      setIsLoadingChatHandle(true);
      setChatHandle(null);
      setChatHandleError(null);

      const dir = await navigator.storage.getDirectory();
      const userDir = await dir.getDirectoryHandle(userId, { create: true });
      setChatHandle(userDir);
    } catch (e) {
      console.error(e);
      setChatHandleError(e);
    } finally {
      setIsLoadingChatHandle(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId || !isChatHandleEnabled) {
      setIsLoadingChatHandle(false);
      setChatHandle(null);
      setChatHandleError(null);
      return;
    }

    if (!chatHandle || chatHandle?.name !== userId) {
      populateChatHandle();
    }
    populateChatHandle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isChatHandleEnabled, populateChatHandle]);

  const clearChatHandle = useCallback(() => {
    setChatHandle(null);
    setIsLoadingChatHandle(false);
    setChatHandleError(null);
  }, []);

  const value = useMemo(
    () => ({
      chatHandle,
      setChatHandle,
      chatHandleError,
      isLoadingChatHandle,
      populateChatHandle,
      clearChatHandle,
    }),
    [
      chatHandle,
      chatHandleError,
      clearChatHandle,
      isLoadingChatHandle,
      populateChatHandle,
    ]
  );

  return (
    <ChatHandleContext.Provider
      {...props}
      value={value}
    ></ChatHandleContext.Provider>
  );
};

export function useChatHandle() {
  const context = useContext(ChatHandleContext);
  if (context === null) {
    throw new Error("useChatHandle must be used within an ChatHandleProvider");
  }
  return context;
}
